import { useTranslation } from 'react-i18next';
import {
  InfiosChip,
  InfiosContainer,
  InfiosLink,
  InfiosTable,
  InfiosTableCell,
  InfiosTableColumn,
  InfiosTableHeaderCell,
  InfiosTextBlock,
} from '@infios/uikit/react';
import type { OrderShipment, OrderShipmentPackage, OrderShipmentsData } from '../../interfaces/chat';
import { FormattedDateTime } from '../FormattedDateTime';
import { getStatusChipColor } from './statusChipColor';
import { getStatusLabel } from './statusLabel';
import { getTrackingUrl } from './trackingUrl';
import styles from './ContentBlocks.module.css';

interface OrderShipmentsBlockProps {
  data: OrderShipmentsData;
}

interface PackageRow extends OrderShipmentPackage {
  id: string;
  carrier: string | undefined;
}

function renderTrackingNumber(row: PackageRow) {
  if (!row.trackingNumber) return '—';
  const url = getTrackingUrl(row.carrier, row.trackingNumber);
  if (!url) return row.trackingNumber;
  return (
    <InfiosLink href={url} target="_blank" showIcon>
      {row.trackingNumber}
    </InfiosLink>
  );
}

export function OrderShipmentsBlock({ data }: OrderShipmentsBlockProps) {
  const { t } = useTranslation();
  const shipments = Array.isArray(data.shipments) ? data.shipments : [];
  if (shipments.length === 0) return null;

  return (
    <InfiosContainer type="card">
      <InfiosTextBlock slot="header" weight="bold">
        {t('ChatbotUI.OrderBlocks.ShipmentsHeading')}
      </InfiosTextBlock>

      <div className={styles.list}>
        {shipments.map((shipment: OrderShipment, idx) => {
          const packageRows: PackageRow[] = Array.isArray(shipment.packages)
            ? shipment.packages.map((pkg, pkgIdx) => ({
                ...pkg,
                id: pkg.trackingNumber ?? String(pkgIdx),
                carrier: shipment.carrier,
              }))
            : [];

          return (
            <InfiosContainer key={shipment.shipmentId ?? shipment.shipmentNumber ?? idx} type="card">
              <div slot="header">
                {shipment.shipmentNumber ? (
                  <InfiosTextBlock weight="bold" size="comfortable">
                    {t('ChatbotUI.OrderBlocks.ShipmentNumberHeading', {
                      shipmentNumber: shipment.shipmentNumber,
                    })}
                  </InfiosTextBlock>
                ) : null}
              </div>
              {shipment.shipmentStatus ? (
                <div slot="header-trailing">
                  <InfiosChip size="compact" {...getStatusChipColor(shipment.shipmentStatus)}>
                    {getStatusLabel(shipment.shipmentStatus, t)}
                  </InfiosChip>
                </div>
              ) : null}

              <div className={styles.grid}>
                {shipment.carrier ? (
                  <InfiosTextBlock size="standard" pretext={t('ChatbotUI.OrderBlocks.Carrier')}>
                    {shipment.carrier}
                  </InfiosTextBlock>
                ) : null}
                {shipment.estimatedDeliveryDate ? (
                  <InfiosTextBlock size="standard" pretext={t('ChatbotUI.OrderBlocks.ExpectedDelivery')}>
                    <FormattedDateTime iso={shipment.estimatedDeliveryDate} />
                  </InfiosTextBlock>
                ) : null}
              </div>

              {packageRows.length > 0 ? (
                <>
                  <br />
                  <InfiosTextBlock weight="bold">{t('ChatbotUI.OrderBlocks.Packages')}</InfiosTextBlock>
                  <InfiosTable dataSource={packageRows}>
                    <InfiosTableColumn columnId="trackingNumber">
                      <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.TrackingNumber')}</InfiosTableHeaderCell>
                      <InfiosTableCell renderer={renderTrackingNumber} />
                    </InfiosTableColumn>
                  </InfiosTable>
                </>
              ) : null}
            </InfiosContainer>
          );
        })}
      </div>
    </InfiosContainer>
  );
}
